import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, Box, TextField, MenuItem, IconButton, List, ListItem, ListItemAvatar, ListItemText, Avatar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CustomCheckBoxV2 from '../../../../components/CheckBoxV2/CheckBox';
import Toast from '../../../../components/Toast/Toast';
import { LANG_TEXT } from '../../../../constants/language';
import { useMenuContext } from '../../../../context/MenuContext';
import { useCategoriesContext } from '../../../../context/CategoriesContext';
import { useSettingsContext } from '../../../../context/SettingsContext';
import { FoodItem } from '../../menu.types';


interface AddRecommendedModalProps {
    open: boolean;
    onClose: () => void;
    onAdd: (items: FoodItem[]) => void;
    currentRecommendations: FoodItem[];
}

export function AddRecommendedModal({ open, onClose, onAdd, currentRecommendations }: AddRecommendedModalProps) {
    const { t } = useTranslation();
    const { foodMenu } = useMenuContext();
    const { foodMenu: allFoodMenu } = useCategoriesContext();
    const { currentSetting } = useSettingsContext();
    const maxRecommendQuantity = currentSetting?.max_recommend_quantity ?? 4;
    const [selectedCategory, setSelectedCategory] = useState('ALL');
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [toast, setToast] = useState<{ open: boolean; variant: 'success' | 'error'; content: string }>({
        open: false,
        variant: 'success',
        content: '',
    });
    const MAX_SELECTED = maxRecommendQuantity;

    useEffect(() => {
        if (open) {
            setSelectedItems(currentRecommendations.map((r) => r.id));
        }
    }, [open, currentRecommendations]);

    const availableItems = useMemo(
        () =>
            foodMenu.foods.map((food) => ({
                id: food.id,
                name: food.name ?? '',
                store: food.sku ?? '',
                image: Array.isArray(food.image_url) ? food.image_url[0] : food.image_url,
                price: food.price,
                banner: false,
                enabled: true,
                restaurantId: food.restaurantId || '',
                order: 0,
            })),
        [foodMenu.foods]
    );

    const filteredItems = useMemo(
        () => {
            if (selectedCategory === 'ALL') return availableItems;
            const currentRecIds = new Set(currentRecommendations.map((r) => r.id));
            const filtered = availableItems.filter((item) => {
                if (currentRecIds.has(item.id)) return true;
                const food = foodMenu.foods.find((f) => f.id === item.id);
                if (!food) return false;
                const categories = Array.isArray(food.categories_menu_ids)
                    ? food.categories_menu_ids.map((cat: any) => typeof cat === 'string' ? cat : cat.id)
                    : [];
                return categories.includes(String(selectedCategory));
            });

            return filtered;
        },
        [selectedCategory, availableItems, foodMenu.foods, currentRecommendations]
    );

    const handleToggleItem = (itemId: string) => {
        setSelectedItems((prev) => {
            const exists = prev.includes(itemId);
            if (exists) return prev.filter((id) => id !== itemId);
            if (prev.length >= MAX_SELECTED) {
                setToast({ open: true, variant: 'error', content: t(LANG_TEXT.ADD_RECOMMEND_LIMIT) });
                return prev;
            }
            return [...prev, itemId];
        });
    };

    const handleConfirm = () => {
        const itemsToAdd = availableItems.filter((item) => selectedItems.includes(item.id));
        onAdd(itemsToAdd);
        setSelectedItems([]);
        onClose();
    };

    const handleClose = () => {
        setSelectedItems([]);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 2 } }}>
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            select
                            value={selectedCategory}
                            onChange={(e) => {
                                setSelectedCategory(e.target.value);
                                if (e.target.value === 'ALL') {
                                    setSelectedItems([]);
                                }
                            }}
                            size="small"
                            sx={{ flex: 1 }}
                        >
                            <MenuItem value="ALL">{t(LANG_TEXT.CATEGORIES)}</MenuItem>
                            {(allFoodMenu.categories.length > 0 ? allFoodMenu.categories : foodMenu.categories).map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                    {cat.title}
                                </MenuItem>
                            ))}
                        </TextField>

                        <IconButton
                            onClick={handleConfirm}
                            disabled={selectedItems.length === 0}
                            sx={{
                                bgcolor: selectedItems.length > 0 ? 'success.main' : 'grey.300',
                                color: 'white',
                                height: 36,
                                width: 36,
                                borderRadius: 1,
                                '&:hover': { bgcolor: selectedItems.length > 0 ? 'success.dark' : 'grey.400' },
                                '&:disabled': { bgcolor: 'grey.300', color: 'white' },
                            }}
                        >
                            <CheckIcon />
                        </IconButton>

                        <IconButton
                            onClick={handleClose}
                            sx={{ bgcolor: 'error.main', height: 36, width: 36, borderRadius: 1, color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <List sx={{ maxHeight: 400, minHeight: 400, overflow: 'auto' }}>
                        {filteredItems.map((item) => (
                            <ListItem
                                key={item.id}
                                onClick={() => handleToggleItem(item.id)}
                                sx={{
                                    borderRadius: 2,
                                    mb: 1,
                                    bgcolor: selectedItems.includes(item.id) ? 'action.selected' : 'transparent',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    cursor: 'pointer',
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar src={item.image} alt={item.name} variant="rounded" sx={{ width: 48, height: 48 }} />
                                </ListItemAvatar>
                                <ListItemText primary={item.name} secondary={item.store} primaryTypographyProps={{ fontWeight: 500 }} />
                                <CustomCheckBoxV2 value={item.id} checked={selectedItems.includes(item.id)} setValue={handleToggleItem} />
                            </ListItem>
                        ))}
                    </List>
                    <Toast
                        open={toast.open}
                        onClose={() => setToast({ ...toast, open: false })}
                        variant={toast.variant}
                        content={toast.content}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
}
